import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cartAPI, orderAPI } from '../../services/marketplaceAPI';
import PaymentGateway from '../../components/payments/PaymentGateway';
import { getImageUrl } from '../../utils/imageUtils';
import { useCart } from '../../contexts/CartContext';
import DashboardButton from '../../components/DashboardButton';
import { api } from '../../utils/api';
import './CheckoutPage.css';



const CheckoutPage = () => {
  const navigate = useNavigate();
  const { fetchCart: refreshCartContext } = useCart(); // Get fetchCart from CartContext to update global cart count
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showGateway, setShowGateway] = useState(false);
  const [pendingOrderMeta, setPendingOrderMeta] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [paymentCompleted] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('checkout-theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  
  // Form states
  const [formData, setFormData] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Bangladesh',
    notes: ''
  });
  const [addressModified, setAddressModified] = useState(false);

  // Loyalty points
  const [useLoyaltyPoints, setUseLoyaltyPoints] = useState(false);
  const [loyaltyPointsToUse, setLoyaltyPointsToUse] = useState(0);
  const [loyaltyPointsValue, setLoyaltyPointsValue] = useState(0);

  // Coupon system
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');

  // Health insurance
  const [insuranceData, setInsuranceData] = useState({
    provider: '',
    policyNumber: '',
    coverage: '',
    validTill: ''
  });
  const [useInsurance, setUseInsurance] = useState(false);
  const [insuranceModified, setInsuranceModified] = useState(false);

  // Available coupons
  const availableCoupons = {
    'Happy20': { discount: 20, type: 'percentage' },
    'Welcome25': { discount: 25, type: 'percentage' }
  };

  // Memoize fetchCart to avoid infinite re-renders
  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await cartAPI.getCart();
      
      if (response.success) {
        if (response.data.items.length === 0) {
          navigate('/cart');
          return;
        }
        setCart(response.data);
      } else {
        setError('Failed to fetch cart');
      }
    } catch (err) {
      console.error('Error fetching cart:', err);
      setError('Error loading cart. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchUserProfile = useCallback(async () => {
    try {
      console.log('🔍 Fetching user profile...');
      
      // Use api utility (same as PatientDashboard) which includes authentication
      try {
        const res = await api.get('/patient-profile/profile');
        
        if (res.data?.success && res.data?.data) {
          console.log('✅ Patient profile loaded successfully');
          console.log('👤 User email:', res.data.data.email);
          console.log('👤 User role:', res.data.data.role);
          console.log('🎁 Loyalty Points:', res.data.data.loyaltyPoints);
          console.log('💳 Billing data:', JSON.stringify(res.data.data.billing, null, 2));
          console.log('📍 Shipping address:', JSON.stringify(res.data.data.billing?.shippingAddress, null, 2));
        
          setUserProfile(res.data.data);
          return; // Success, exit early
        }
      } catch (profileError) {
        console.log('⚠️ Patient profile endpoint failed, trying check-auth as fallback...', profileError);
        
        // Fallback to check-auth if patient profile fails
        try {
          const authRes = await api.get('/auth/check-auth');
          
          if (authRes.data?.success && authRes.data?.user) {
            console.log('✅ Auth check profile loaded');
            console.log('👤 User email:', authRes.data.user.email);
            console.log('👤 User role:', authRes.data.user.role);
            console.log('🎁 Loyalty Points:', authRes.data.user.loyaltyPoints);
            console.log('💳 Billing data:', JSON.stringify(authRes.data.user.billing, null, 2));
            
            setUserProfile(authRes.data.user);
        } else {
          console.error('❌ Auth check returned success=false or no user');
          setUserProfile({});
        }
        } catch (authError) {
          console.error('❌ Both endpoints failed');
        setUserProfile({});
        }
      }
    } catch (err) {
      console.error('❌ Error fetching user profile:', err);
      setUserProfile({});
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('checkout-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  useEffect(() => {
    // Check if payment was recently completed
    const recentPayment = localStorage.getItem('recentPaymentCompleted');
    if (recentPayment) {
      const paymentTime = JSON.parse(recentPayment);
      const now = Date.now();
      // If payment was completed within last 5 minutes, redirect
      if (now - paymentTime < 5 * 60 * 1000) {
        setRedirecting(true);
        navigate('/dashboard');
        return;
      } else {
        // Clear old payment record
        localStorage.removeItem('recentPaymentCompleted');
      }
    }
    
    fetchCart();
    fetchUserProfile();
  }, [fetchCart, fetchUserProfile, navigate]);

  // Redirect if payment is already completed
  useEffect(() => {
    if (paymentCompleted) {
      navigate('/dashboard');
    }
  }, [paymentCompleted, navigate]);

  useEffect(() => {
    console.log('=== AUTOFILL DEBUG: UserProfile changed ===');
    console.log('Full userProfile:', JSON.stringify(userProfile, null, 2));
    
    if (userProfile) {
      console.log('Checking for billing address in userProfile...');
      console.log('userProfile.billing:', userProfile.billing);
      console.log('userProfile.billing?.shippingAddress:', userProfile.billing?.shippingAddress);
      
      // Try different possible locations for address data
      let address = null;
      
      // First try billing.shippingAddress
      if (userProfile.billing?.shippingAddress) {
        address = userProfile.billing.shippingAddress;
        console.log('✅ Found address in billing.shippingAddress:', address);
      }
      // Then try billing.address
      else if (userProfile.billing?.address) {
        address = userProfile.billing.address;
        console.log('✅ Found address in billing.address:', address);
      }
      // Then try direct address
      else if (userProfile.address) {
        address = userProfile.address;
        console.log('✅ Found address in userProfile.address:', address);
      }
      // Then try direct shippingAddress
      else if (userProfile.shippingAddress) {
        address = userProfile.shippingAddress;
        console.log('✅ Found address in userProfile.shippingAddress:', address);
      }
      // Then try patientDetails.address
      else if (userProfile.patientDetails?.address) {
        address = userProfile.patientDetails.address;
        console.log('✅ Found address in patientDetails.address:', address);
      }
      
      if (address) {
        console.log('✅ AUTO-FILLING form with address:', address);
        const newFormData = {
          street: address.street || address.address || '',
          city: address.city || '',
          state: address.state || '',
          zipCode: address.zipCode || address.postalCode || address.pincode || '',
          country: address.country || 'Bangladesh',
          notes: ''
        };
        console.log('New form data:', newFormData);
        setFormData(newFormData);
        // Reset address modified flag since data is loaded from profile
        setAddressModified(false);
        console.log('✅ Form data set successfully - address should be auto-filled!');
      } else {
        console.log('❌ No address found in user profile - setting empty values');
        // Set default values
        setFormData({
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'Bangladesh',
          notes: ''
        });
        // Reset address modified flag
        setAddressModified(false);
      }
      
      // Auto-fill health insurance data if available
      if (userProfile.billing?.insurance) {
        const insurance = userProfile.billing.insurance;
        console.log('Found insurance data in profile:', insurance);
        setInsuranceData({
          provider: insurance.provider || '',
          policyNumber: insurance.policyNumber || '',
          coverage: insurance.coverage || '',
          validTill: insurance.validTill || ''
        });
        // Reset insurance modified flag since data is loaded from profile
        setInsuranceModified(false);
        console.log('Insurance data auto-filled successfully');
      } else {
        console.log('No insurance data found in profile');
        // Keep current insurance data or set defaults
        setInsuranceData({
          provider: '',
          policyNumber: '',
          coverage: '',
          validTill: ''
        });
        // Reset insurance modified flag
        setInsuranceModified(false);
      }
    }
  }, [userProfile]);

  useEffect(() => {
    console.log('🔄 Loyalty points effect triggered:', {
      useLoyaltyPoints,
      userProfileLoyaltyPoints: userProfile?.loyaltyPoints,
      cartTotalAmount: cart?.totalAmount
    });
    
    if (userProfile && useLoyaltyPoints && (userProfile.loyaltyPoints || 0) > 0) {
      const maxPoints = Math.min(userProfile.loyaltyPoints || 0, Math.floor(cart?.totalAmount || 0));
      console.log('💰 Calculating loyalty points:', { maxPoints, discount: maxPoints * 0.1 });
      setLoyaltyPointsToUse(maxPoints);
      setLoyaltyPointsValue(maxPoints * 0.1); // 1 point = 0.1 currency
    } else {
      console.log('❌ Resetting loyalty points (disabled or no points)');
      setLoyaltyPointsToUse(0);
      setLoyaltyPointsValue(0);
    }
  }, [useLoyaltyPoints, userProfile, cart]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setAddressModified(true);
  };

  const saveAddressToProfile = async () => {
    try {
      console.log('💾 Saving address to profile:', formData);
      
      const addressData = {
        billing: {
          shippingAddress: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            country: formData.country
          }
        }
      };
      
      console.log('Sending address data:', JSON.stringify(addressData, null, 2));
      
      const response = await fetch('/api/auth/update-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(addressData)
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        
        if (data.success) {
          console.log('✅ Address saved to profile successfully');
          // Update the local userProfile state
          setUserProfile(prev => ({
            ...prev,
            billing: {
              ...prev.billing,
              shippingAddress: addressData.billing.shippingAddress
            }
          }));
          // Reset the modified flag
          setAddressModified(false);
          alert('Shipping address saved to your profile for future use!');
        } else {
          console.error('❌ Failed to save address:', data.message);
          alert('Failed to save address: ' + data.message);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to save address, status:', response.status, 'error:', errorText);
        alert('Failed to save address. Please try again.');
      }
    } catch (err) {
      console.error('❌ Error saving address to profile:', err);
      alert('Error saving address. Please try again.');
    }
  };

  const saveInsuranceToProfile = async () => {
    try {
      console.log('Saving insurance to profile:', insuranceData);
      
      const insuranceUpdateData = {
        billing: {
          insurance: {
            provider: insuranceData.provider,
            policyNumber: insuranceData.policyNumber,
            coverage: insuranceData.coverage,
            validTill: insuranceData.validTill
          }
        }
      };
      
      const response = await fetch('/api/patient/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(insuranceUpdateData)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('Insurance saved to profile successfully');
          // Update the local userProfile state
          setUserProfile(prev => ({
            ...prev,
            billing: {
              ...prev.billing,
              insurance: insuranceUpdateData.billing.insurance
            }
          }));
          // Reset the modified flag
          setInsuranceModified(false);
          alert('Health insurance information saved to your profile for future use!');
        } else {
          console.error('Failed to save insurance:', data.message);
          alert('Failed to save insurance: ' + data.message);
        }
      } else {
        console.error('Failed to save insurance, status:', response.status);
        alert('Failed to save insurance. Please try again.');
      }
    } catch (err) {
      console.error('Error saving insurance to profile:', err);
      alert('Error saving insurance. Please try again.');
    }
  };

  const handleInsuranceChange = (e) => {
    const { name, value } = e.target;
    setInsuranceData(prev => ({
      ...prev,
      [name]: value
    }));
    setInsuranceModified(true);
  };

  const applyCoupon = () => {
    setCouponError('');
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    const coupon = availableCoupons[couponCode.trim()];
    if (!coupon) {
      setCouponError('Invalid coupon code');
      return;
    }

    setAppliedCoupon({ code: couponCode.trim(), ...coupon });
    setCouponCode('');
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
  };

  const calculateTotal = () => {
    if (!cart) return 0;
    
    let total = cart.totalAmount;
    
    // Apply coupon discount
    if (appliedCoupon) {
      if (appliedCoupon.type === 'percentage') {
        total = total * (1 - appliedCoupon.discount / 100);
      }
    }
    
    // Apply loyalty points
    total = Math.max(0, total - loyaltyPointsValue);
    
    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if payment is already completed
    if (paymentCompleted) {
      navigate('/dashboard');
      return;
    }
    
    // Validate form
    if (!formData.street || !formData.city || !formData.state || !formData.zipCode) {
      setError('Please fill in all required address fields');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Save shipping data locally and open payment gateway
      const shippingAddress = {
        street: formData.street.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zipCode: formData.zipCode.trim(),
        country: formData.country.trim()
      };

      const orderMeta = {
        shippingAddress,
        notes: formData.notes.trim(),
        amount: calculateTotal(),
        originalAmount: cart.totalAmount,
        loyaltyPointsUsed: loyaltyPointsToUse,
        couponApplied: appliedCoupon,
        insuranceData: useInsurance ? insuranceData : null
      };

      setPendingOrderMeta(orderMeta);
      // Only open gateway if payment is not completed
      if (!paymentCompleted) {
        setShowGateway(true);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Error creating order:', err);
      setError('Error creating order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Prevent payment gateway from opening if payment is completed
  const handleGatewayClose = () => {
    if (paymentCompleted) {
      navigate('/dashboard');
    } else {
      setShowGateway(false);
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      console.log('Payment successful, creating order with data:', paymentData);
      
      // Close the payment gateway immediately
      setShowGateway(false);
      setSubmitting(false);
      
      // Validate required data
      if (!pendingOrderMeta || !pendingOrderMeta.shippingAddress) {
        setError('Missing order information. Please try again.');
        return;
      }
      
      if (!paymentData.transactionId && !paymentData.orderId) {
        setError('Payment transaction ID is missing. Please contact support.');
        return;
      }
      
      const payload = {
        shippingAddress: pendingOrderMeta.shippingAddress,
        notes: pendingOrderMeta.notes,
        loyaltyPointsUsed: pendingOrderMeta.loyaltyPointsUsed,
        couponApplied: pendingOrderMeta.couponApplied,
        insuranceData: pendingOrderMeta.insuranceData,
        payment: {
          transactionId: paymentData.orderId || paymentData.transactionId,
          paymentMethod: paymentData.paymentMethod,
          details: paymentData.details || {},
        },
        amount: pendingOrderMeta.amount,
        originalAmount: pendingOrderMeta.originalAmount
      };
      
      console.log('Creating order with payload:', payload);
      
      // Validate payload
      if (!payload.shippingAddress.street || !payload.shippingAddress.city || 
          !payload.shippingAddress.state || !payload.shippingAddress.zipCode) {
        setError('Shipping address is incomplete. Please try again.');
        return;
      }
      
      if (!payload.payment.transactionId) {
        setError('Payment transaction ID is missing. Please contact support.');
        return;
      }
      
      // Add loading state for order creation
      setSubmitting(true);
      setError('');
      
      // Create order after payment
      try {
        console.log('Creating order with payload:', payload);
        console.log('Payment data being sent:', {
          orderId: payload.orderId,
          amount: payload.amount,
          paymentMethod: payload.paymentMethod,
          paymentType: payload.paymentType,
          transactionId: payload.transactionId,
          details: payload.details
        });
        console.log('Payment method validation:', {
          paymentMethod: payload.paymentMethod,
          isValidMethod: ['mobile', 'card', 'bank', 'bKash', 'Rocket', 'Nagad', 'Qcash'].includes(payload.paymentMethod)
        });
        console.log('Payment data structure:', {
          hasOrderId: !!payload.orderId,
          hasAmount: !!payload.amount,
          hasPaymentMethod: !!payload.paymentMethod,
          hasPaymentType: !!payload.paymentType,
          hasTransactionId: !!payload.transactionId,
          hasDetails: !!payload.details
        });
        console.log('Payment data types:', {
          orderId: typeof payload.orderId,
          amount: typeof payload.amount,
          paymentMethod: typeof payload.paymentMethod,
          paymentType: typeof payload.paymentType,
          transactionId: typeof payload.transactionId,
          details: typeof payload.details
        });
        console.log('Payment data validation:', {
          hasOrderId: !!payload.orderId,
          hasAmount: !!payload.amount,
          hasPaymentMethod: !!payload.paymentMethod,
          hasPaymentType: !!payload.paymentType,
          hasTransactionId: !!payload.transactionId,
          hasDetails: !!payload.details
        });
        console.log('Payment method enum validation:', {
          paymentMethod: payload.paymentMethod,
          validMethods: ['mobile', 'card', 'bank', 'bKash', 'Rocket', 'Nagad', 'Qcash'],
          isValid: ['mobile', 'card', 'bank', 'bKash', 'Rocket', 'Nagad', 'Qcash'].includes(payload.paymentMethod)
        });
        console.log('Payment data structure validation:', {
          orderId: {
            value: payload.orderId,
            type: typeof payload.orderId,
            hasValue: !!payload.orderId
          },
          amount: {
            value: payload.amount,
            type: typeof payload.amount,
            hasValue: !!payload.amount
          },
          paymentMethod: {
            value: payload.paymentMethod,
            type: typeof payload.paymentMethod,
            hasValue: !!payload.paymentMethod
          },
          paymentType: {
            value: payload.paymentType,
            type: typeof payload.paymentType,
            hasValue: !!payload.paymentType
          },
          transactionId: {
            value: payload.transactionId,
            type: typeof payload.transactionId,
            hasValue: !!payload.transactionId
          },
          details: {
            value: payload.details,
            type: typeof payload.details,
            hasValue: !!payload.details
          }
        });
        console.log('Payment data enum validation:', {
          paymentMethod: {
            value: payload.paymentMethod,
            validMethods: ['mobile', 'card', 'bank', 'bKash', 'Rocket', 'Nagad', 'Qcash'],
            isValid: ['mobile', 'card', 'bank', 'bKash', 'Rocket', 'Nagad', 'Qcash'].includes(payload.paymentMethod)
          },
          paymentType: {
            value: payload.paymentType,
            validTypes: ['marketplace', 'consultation', 'test'],
            isValid: ['marketplace', 'consultation', 'test'].includes(payload.paymentType)
          }
        });
        console.log('Payment data final validation summary:', {
          allFieldsPresent: !!payload.orderId && !!payload.amount && !!payload.paymentMethod && !!payload.paymentType && !!payload.transactionId,
          allFieldsValid: 
            typeof payload.orderId === 'string' && payload.orderId.length > 0 &&
            typeof payload.amount === 'number' && payload.amount > 0 &&
            ['mobile', 'card', 'bank', 'bKash', 'Rocket', 'Nagad', 'Qcash'].includes(payload.paymentMethod) &&
            ['marketplace', 'consultation', 'test'].includes(payload.paymentType) &&
            typeof payload.transactionId === 'string' && payload.transactionId.length > 0,
          paymentMethodEnum: ['mobile', 'card', 'bank', 'bKash', 'Rocket', 'Nagad', 'Qcash'],
          paymentTypeEnum: ['marketplace', 'consultation', 'test']
        });
        
        const response = await orderAPI.createOrderAfterPayment(payload);
        console.log('Order creation response:', response);
        
        if (response.success) {
          setOrderCreated(true);
          setOrderId(response.data.orderId);
          setShowSuccessModal(true);
          
          // Save shipping address and insurance to user profile for future use
          if (addressModified || insuranceModified) {
            try {
              const profileUpdatePayload = {};
              
              if (addressModified) {
                profileUpdatePayload.billing = {
                  ...userProfile.billing,
                  shippingAddress: payload.shippingAddress
                };
              }
              
              if (insuranceModified && payload.insuranceData) {
                if (!profileUpdatePayload.billing) {
                  profileUpdatePayload.billing = { ...userProfile.billing };
                }
                profileUpdatePayload.billing.insurance = payload.insuranceData;
              }
              
              await fetch('/api/patient/profile', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(profileUpdatePayload)
              });
              console.log('Shipping address and insurance saved to profile');
            } catch (saveError) {
              console.error('Error saving address/insurance to profile:', saveError);
              // Don't fail the order if profile update fails
            }
          }
          
          // Clear cart after successful order
          try {
            await cartAPI.clearCart();
            console.log('Cart cleared successfully');
            
            // Refresh cart context to update global cart count (badge)
            await refreshCartContext();
            console.log('Cart context refreshed - badge should update');
          } catch (clearError) {
            console.error('Error clearing cart:', clearError);
          }
          
          // Refresh local cart data
          fetchCart();
        } else {
          setError(response.message || 'Failed to create order');
        }
      } catch (error) {
        console.error('Post-payment order creation failed', error);
        
        // Provide more detailed error information
        let errorMessage = 'Order creation failed after payment. ';
        
        if (error.response) {
          // Server responded with error
          const serverError = error.response.data;
          errorMessage += `Server error: ${serverError.message || serverError.error || 'Unknown server error'}`;
          console.error('Server error details:', serverError);
        } else if (error.request) {
          // Request was made but no response received
          errorMessage += 'No response from server. Please check your internet connection.';
          console.error('Network error:', error.request);
        } else {
          // Something else happened
          errorMessage += `Error: ${error.message || 'Unknown error occurred'}`;
          console.error('Other error:', error);
        }
        
        setError(errorMessage);
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Post-payment order creation failed', err);
      
      // Provide more detailed error information
      let errorMessage = 'Order creation failed after payment. ';
      
      if (err.response) {
        // Server responded with error
        const serverError = err.response.data;
        errorMessage += `Server error: ${serverError.message || serverError.error || 'Unknown server error'}`;
        console.error('Server error details:', serverError);
      } else if (err.request) {
        // Request was made but no response received
        errorMessage += 'No response from server. Please check your internet connection.';
        console.error('Network error:', err.request);
      } else {
        // Something else happened
        errorMessage += `Error: ${err.message || 'Unknown error occurred'}`;
        console.error('Other error:', err);
      }
      
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  if (loading || redirecting) {
    return (
      <div className={`loading-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner"></div>
          <div style={{ color: isDarkMode ? '#9ca3af' : '#6B6B6B', marginTop: '1rem' }}>
            {redirecting ? 'Redirecting to dashboard...' : 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className={`empty-cart ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <div style={{ textAlign: 'center' }}>
          <div className="empty-cart-text">Your cart is empty</div>
          <button
            onClick={() => navigate('/marketplace')}
            className="checkout-btn checkout-btn-primary"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  const total = calculateTotal();

  return (
    <div className={`checkout-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Dashboard Button */}
      <DashboardButton userRole="Patient" />
      
      {/* Dark Mode Toggle */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle-checkout"
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

        {/* Header */}
      <div className={`checkout-header ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="checkout-header-content">
          <h1>
            <span>🛒</span>
            Checkout
          </h1>
          <p>Complete your order</p>
        </div>
        </div>

      <div className="checkout-container">
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {paymentCompleted && (
          <div className="error-message" style={{ background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', borderColor: '#10b981', color: '#059669' }}>
            ✅ Payment completed successfully! Redirecting to marketplace...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="checkout-layout">
            {/* Left Column - Checkout Form */}
            <div className="checkout-left">
              {/* Shipping Address */}
              <div className="checkout-card">
                <div className="checkout-card-header">
                  <div className="checkout-card-title">
                    <span className="checkout-card-icon">🚚</span>
                      Shipping Address
                  </div>
                </div>
                
                <div className="checkout-form-grid">
                  <div className="checkout-form-field checkout-form-field-full">
                    <label>Street Address *</label>
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your street address"
                    />
                  </div>
                  
                  <div className="checkout-form-field">
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your city"
                    />
                  </div>
                  
                  <div className="checkout-form-field">
                    <label>State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your state"
                    />
                  </div>
                  
                  <div className="checkout-form-field">
                    <label>ZIP Code *</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter ZIP code"
                    />
                  </div>
                  
                  <div className="checkout-form-field">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder="Enter country"
                    />
                  </div>
                  
                  <div className="checkout-form-field checkout-form-field-full">
                    <label>Order Notes (Optional)</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Any special instructions for your order..."
                      rows={3}
                    />
                  </div>
                  
                  {/* Save Address Button */}
                  {((!userProfile?.billing?.shippingAddress) || addressModified) && (
                    <div className="checkout-form-field checkout-form-field-full" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                      <button
                        type="button"
                        className="checkout-btn checkout-btn-outline"
                          onClick={saveAddressToProfile}
                          disabled={!formData.street || !formData.city || !formData.state || !formData.zipCode}
                      >
                        💾 Save Address to Profile
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Loyalty Points Section */}
              <div className="checkout-card">
                <div className="checkout-card-header">
                  <div className="checkout-card-title">
                    <span className="checkout-card-icon">🎁</span>
                      Loyalty Points
                  </div>
                  <div className="checkout-switch">
                    <label className="checkout-switch-label">Use Points</label>
                    <label className="checkout-toggle-switch">
                      <input
                        type="checkbox"
                        checked={useLoyaltyPoints}
                        onChange={(e) => setUseLoyaltyPoints(e.target.checked)}
                        disabled={!userProfile?.loyaltyPoints || (userProfile?.loyaltyPoints || 0) === 0}
                      />
                      <span className="checkout-toggle-slider"></span>
                    </label>
                  </div>
                </div>
                
                <div>
                  {/* Always show current loyalty points */}
                  <div style={{ 
                    marginBottom: '1rem', 
                    padding: '1rem', 
                    background: isDarkMode ? 'rgba(30, 58, 138, 0.3)' : 'rgba(219, 234, 254, 0.6)', 
                    borderRadius: '0.75rem', 
                    border: `2px solid ${isDarkMode ? 'rgba(96, 165, 250, 0.4)' : 'rgba(96, 165, 250, 0.3)'}` 
                  }}>
                    <p style={{ 
                      marginBottom: '0.5rem', 
                      color: isDarkMode ? '#93c5fd' : '#1e3a8a',
                      fontSize: '1.1rem',
                      fontWeight: '700'
                    }}>
                      Your Current Points: <strong style={{ fontSize: '1.3rem' }}>{userProfile?.loyaltyPoints || 0}</strong>
                    </p>
                    {(userProfile?.loyaltyPoints || 0) > 0 ? (
                      <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.9rem' }}>
                        You can redeem up to {Math.min(userProfile.loyaltyPoints || 0, Math.floor(cart?.totalAmount || 0))} points for this order
                      </p>
                    ) : (
                      <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.9rem' }}>
                        Make purchases to earn loyalty points!
                      </p>
                    )}
                  </div>
                    
                  {useLoyaltyPoints && (userProfile?.loyaltyPoints || 0) > 0 && (
                    <div style={{ 
                      marginBottom: '1rem', 
                      padding: '1rem', 
                      background: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)', 
                      borderRadius: '0.75rem', 
                      border: `2px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)'}` 
                    }}>
                      <p style={{ color: '#10b981', fontWeight: '700', marginBottom: '0.5rem', fontSize: '1.05rem' }}>
                        ✓ Using {loyaltyPointsToUse} points = ৳{loyaltyPointsValue.toFixed(2)} discount
                      </p>
                      <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                          Points will be deducted from your account after order completion
                      </p>
                    </div>
                    )}
                    
                  <div className="loyalty-info">
                    <p className="loyalty-info-text">
                        <strong>Earn {Math.floor((cart?.totalAmount || 0) / 100) * 2} points</strong> from this purchase!
                    </p>
                    <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#94a3b8' : '#64748b', marginTop: '0.25rem' }}>
                        (2 points per ৳100 spent)
                    </p>
                  </div>
                </div>
              </div>

              {/* Health Insurance */}
              <div className="checkout-card">
                <div className="checkout-card-header">
                  <div className="checkout-card-title">
                    <span className="checkout-card-icon">🏥</span>
                      Health Insurance
                  </div>
                  <div className="checkout-switch">
                    <label className="checkout-switch-label">Use Insurance</label>
                    <input
                      type="checkbox"
                        checked={useInsurance}
                        onChange={(e) => setUseInsurance(e.target.checked)}
                      style={{ width: '48px', height: '24px', cursor: 'pointer' }}
                  />
                  </div>
                </div>

                {useInsurance && (
                  <div className="checkout-form-grid">
                    <div className="checkout-form-field">
                      <label>Insurance Provider</label>
                      <input
                        type="text"
                        name="provider"
                        value={insuranceData.provider}
                        onChange={handleInsuranceChange}
                        placeholder="Enter insurance provider"
                      />
                    </div>
                    
                    <div className="checkout-form-field">
                      <label>Policy Number</label>
                      <input
                        type="text"
                        name="policyNumber"
                        value={insuranceData.policyNumber}
                        onChange={handleInsuranceChange}
                        placeholder="Enter policy number"
                      />
                    </div>
                    
                    <div className="checkout-form-field">
                      <label>Coverage Amount</label>
                      <input
                        type="text"
                        name="coverage"
                        value={insuranceData.coverage}
                        onChange={handleInsuranceChange}
                        placeholder="Enter coverage amount"
                      />
                    </div>
                    
                    <div className="checkout-form-field">
                      <label>Valid Till</label>
                      <input
                        type="date"
                        name="validTill"
                        value={insuranceData.validTill}
                        onChange={handleInsuranceChange}
                      />
                    </div>
                    
                    {/* Save Insurance Button */}
                    {((!userProfile?.billing?.insurance) || insuranceModified) && (
                      <div className="checkout-form-field checkout-form-field-full" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                        <button
                          type="button"
                          className="checkout-btn checkout-btn-outline"
                            onClick={saveInsuranceToProfile}
                            disabled={!insuranceData.provider || !insuranceData.policyNumber || !insuranceData.coverage || !insuranceData.validTill}
                        >
                          💾 Save Insurance to Profile
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="checkout-right">
              <div className="checkout-card order-summary-card">
                <div className="checkout-card-header">
                  <div className="checkout-card-title">
                    <span className="checkout-card-icon">📋</span>
                  Order Summary
                  </div>
                </div>
                
                {/* Order Items */}
                <div className="order-summary-items">
                  {cart.items.map((item) => (
                    <div key={item.productId._id} className="order-item">
                      <img
                        src={getImageUrl(item.productId?.image)}
                        alt={item.productId?.name || 'Product'}
                        className="order-item-image"
                        onError={(e) => {
                          e.target.src = '/placeholder-product.png';
                        }}
                      />
                      <div className="order-item-details">
                        <div className="order-item-name">{item.productId.name}</div>
                        <div className="order-item-meta">
                          Qty: {item.quantity} × ৳{item.price}
                        </div>
                      </div>
                      <div className="order-item-price">
                        ৳{(item.price * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Coupon Section */}
                <div className="coupon-section">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>🎫</span>
                    <span style={{ fontWeight: '600', color: isDarkMode ? '#93c5fd' : '#1e3a8a' }}>Apply Coupon</span>
                  </div>
                  
                  {appliedCoupon ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                      <div className="coupon-chip">
                        {appliedCoupon.code} ({appliedCoupon.discount}% off)
                      </div>
                      <button
                        type="button"
                        className="checkout-btn checkout-btn-secondary"
                        onClick={removeCoupon}
                        style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="coupon-input-group">
                      <input
                        type="text"
                        className="coupon-input"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                      />
                      <button
                        type="button"
                        className="checkout-btn checkout-btn-outline"
                        onClick={applyCoupon}
                        disabled={!couponCode.trim()}
                        style={{ padding: '0.75rem 1.25rem', whiteSpace: 'nowrap' }}
                      >
                        Apply
                      </button>
                    </div>
                  )}

                  {couponError && (
                    <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem' }}>{couponError}</p>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="price-breakdown">
                  <div className="price-row">
                    <span className="price-label">Subtotal</span>
                    <span className="price-value">৳{cart.totalAmount.toFixed(2)}</span>
                  </div>
                  
                  {appliedCoupon && (
                    <div className="price-row">
                      <span className="price-label" style={{ color: '#10b981' }}>
                        Coupon Discount ({appliedCoupon.discount}%)
                      </span>
                      <span className="price-value" style={{ color: '#10b981' }}>
                        -৳{(cart.totalAmount * appliedCoupon.discount / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  {useLoyaltyPoints && (
                    <div className="price-row">
                      <span className="price-label" style={{ color: '#10b981' }}>
                        Loyalty Points
                      </span>
                      <span className="price-value" style={{ color: '#10b981' }}>
                        -৳{loyaltyPointsValue.toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  <div className="price-row">
                    <span className="price-label">Shipping</span>
                    <span className="price-value" style={{ color: '#10b981' }}>Free</span>
                  </div>
                  
                  <div className="price-total">
                    <span>Total</span>
                    <span>৳{total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Place Order Button */}
                <button
                  type="submit"
                  className="checkout-btn checkout-btn-primary"
                  disabled={submitting || paymentCompleted}
                  style={{ width: '100%', marginBottom: '1rem', padding: '1rem' }}
                >
                  {submitting ? '⏳ Processing...' : paymentCompleted ? '✅ Payment Completed' : '💳 Proceed to Payment'}
                </button>

                <button
                  type="button"
                  className="checkout-btn checkout-btn-secondary"
                  onClick={() => navigate('/cart')}
                  style={{ width: '100%' }}
                >
                  ← Back to Cart
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      
      <PaymentGateway 
        open={showGateway}
        onClose={handleGatewayClose}
        amount={total}
        paymentType="marketplace"
        onSuccess={handlePaymentSuccess}
        userProfile={userProfile}
      />
    </div>
  );
};

export default CheckoutPage;
